import { useEffect } from 'react';
import { ScheduleQueries, ScheduleTypes } from '@frontend/api-schedule';
import { AdaptoActions } from '@frontend/adapto-actions';
import { Actions } from '@frontend/contact-actions';
import { useTranslation } from '@frontend/i18n';
import { useAlert } from '@frontend/design-system';
import { useScheduleAlertsContext } from '../../../../context/ScheduleAlertsContext';
import { scheduleAlertsTrackingIds } from './schedule-actions-tracking-ids';

interface AlertMenuProps {
  alert: ScheduleTypes.ScheduleAlertType;
  locationId: string;
  closeScheduleActionsModal: () => void;
  onViewAppointment: () => void;
}

export const ScheduleAlertActionMenu = ({
  alert,
  locationId,
  closeScheduleActionsModal,
  onViewAppointment,
}: AlertMenuProps) => {
  const { t } = useTranslation('scheduleCalendarActions');
  const updateNotification = useAlert();
  const { clearAlert, setSelectedLocationId, refetchScheduleAlerts } = useScheduleAlertsContext();

  useEffect(() => {
    setSelectedLocationId(locationId);
  }, [locationId]);

  const handleClearAlert = (alertId: string) => {
    clearAlert(alertId, locationId);
  };

  const { mutateAsync: updateAppointmentStatus } = ScheduleQueries.useMutateUpdateAppointmentStatus(
    alert.appointment.id ?? ''
  );

  const handleConfirmAppointment = () => {
    if (!alert.appointment) return;
    const data = {
      status_id: 'Confirmed',
      status: 'confirmed',
      source_id: alert.source_id ?? '',
      source_type: 'employee',
      location_id: locationId,
    } as ScheduleTypes.AppointmentStatusRequest;
    try {
      updateAppointmentStatus(data);
      updateNotification.success(t('Appointment status updated. It might take a few minutes to reflect'));
      refetchScheduleAlerts();
    } catch (error) {
      console.error('Error updating appointment status', error);
      updateNotification.error(t('Error updating appointment status'));
    }
  };

  const fullName = `${alert?.patient.preferred_name ? alert?.patient.preferred_name : alert?.patient.first_name} ${
    alert?.patient.last_name
  }`;

  return (
    <>
      <Actions.Message
        label={t('Send Message')}
        context={{
          personId: alert.patient.person_id ?? '',
          threadGroupId: locationId,
        }}
        onClick={() => closeScheduleActionsModal()}
        trackingIdSuffix={scheduleAlertsTrackingIds.sendMessage}
      />
      <Actions.Call
        label={t('Call {{fullName}}', { fullName })}
        context={{ personId: alert.patient.person_id ?? '' }}
        isInsidePopoverDropdown
        trackingIdSuffix={scheduleAlertsTrackingIds.callPerson}
      />
      <AdaptoActions.Action
        icon='view'
        label={t('View Appointment')}
        onClick={onViewAppointment}
        trackingId={scheduleAlertsTrackingIds.viewAppointment}
      />
      <AdaptoActions.Action
        icon='calendar-confirmed'
        label={t('Confirm Appointment')}
        // NOTE: This is disabled for now until we can know the status update is successful
        disabled={true}
        // disabled={alert.appointment.status.toUpperCase() !== 'UNCONFIRMED'}
        onClick={() => handleConfirmAppointment()}
        trackingId={scheduleAlertsTrackingIds.confirmAppointment}
      />
      <AdaptoActions.Action
        icon='trash'
        label={t('Clear Alert')}
        destructive
        onClick={() => handleClearAlert(alert.id)}
        trackingId={scheduleAlertsTrackingIds.clearAlert}
      />
    </>
  );
};
